import React, { useState } from 'react';
import PropTypes from 'prop-types';
import './BCReferenceModal.css';

// The 5 BC fields used to generate the reason summary
const BC_SUMMARY_FIELDS = [
  { key: 'bc_high_level_overview',  label: 'High-Level Overview' },
  { key: 'bc_situation_description', label: 'Situation Description' },
  { key: 'bc_why_made_change',       label: 'Why Was the Change Made?' },
  { key: 'bc_customer_impact',       label: 'Customer Impact' },
  { key: 'bc_customer_actions',      label: 'What Customers Must Do' },
];

// Hint fields shown alongside release_update_breaking_change
const BC_HINT_FIELDS = [
  { key: 'bc_perceived_loss_of_function', label: 'Perceived Loss of Function?' },
  { key: 'bc_customer_work_duration',     label: 'Expected Customer Work Duration' },
];

function buildSummary(answers) {
  const parts = BC_SUMMARY_FIELDS
    .map(f => answers[f.key] ? `${f.label}: ${answers[f.key]}` : null)
    .filter(Boolean);
  return parts.join('\n\n');
}

const BCReferenceModal = ({ bcRequestNumber, productName, skiRelease, bcAnswers, onApplyReason, onClose }) => {
  const [summary, setSummary] = useState('');
  const [generated, setGenerated] = useState(false);

  const handleGenerate = () => {
    setSummary(buildSummary(bcAnswers));
    setGenerated(true);
  };

  const handleApply = () => {
    onApplyReason(summary);
    onClose();
  };

  const hintValues = BC_HINT_FIELDS.filter(f => bcAnswers[f.key]);

  return (
    <div className="bc-ref-modal__overlay">
      <div className="bc-ref-modal">
        <div className="bc-ref-modal__header">
          <div>
            <h3 className="bc-ref-modal__title">Verified Breaking Change Reference</h3>
            <p className="bc-ref-modal__subtitle">
              <strong>{bcRequestNumber}</strong> — {productName}{skiRelease ? ` / ${skiRelease}` : ''}
            </p>
          </div>
          <button className="bc-ref-modal__close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="bc-ref-modal__body">
          {/* Summary source fields */}
          <section className="bc-ref-modal__section">
            <h4 className="bc-ref-modal__section-title">Breaking Change Details</h4>
            {BC_SUMMARY_FIELDS.map(f => bcAnswers[f.key] ? (
              <div key={f.key} className="bc-ref-modal__field">
                <span className="bc-ref-modal__field-label">{f.label}</span>
                <p className="bc-ref-modal__field-value">{bcAnswers[f.key]}</p>
              </div>
            ) : null)}
          </section>

          {/* Hint fields for release_update_breaking_change */}
          {hintValues.length > 0 && (
            <section className="bc-ref-modal__section bc-ref-modal__section--hint">
              <h4 className="bc-ref-modal__section-title">
                Hints for "Release Update Breaking Change" field
              </h4>
              {hintValues.map(f => (
                <div key={f.key} className="bc-ref-modal__field">
                  <span className="bc-ref-modal__field-label">{f.label}</span>
                  <p className="bc-ref-modal__field-value">{bcAnswers[f.key]}</p>
                </div>
              ))}
            </section>
          )}

          {/* Summary generation */}
          <section className="bc-ref-modal__section">
            <h4 className="bc-ref-modal__section-title">Generate Summary for "Reason" Field</h4>
            <p className="bc-ref-modal__help">
              Summarises the fields above into a single statement you can apply directly to the CuC reason field.
              You can edit the summary before applying.
            </p>
            {!generated ? (
              <button className="bc-ref-modal__btn bc-ref-modal__btn--generate" onClick={handleGenerate}>
                Generate Summary
              </button>
            ) : (
              <>
                <textarea
                  className="bc-ref-modal__summary-textarea"
                  value={summary}
                  onChange={e => setSummary(e.target.value)}
                  rows={8}
                />
                <div className="bc-ref-modal__summary-actions">
                  <button className="bc-ref-modal__btn bc-ref-modal__btn--primary" onClick={handleApply}>
                    Apply to Reason Field
                  </button>
                  <button className="bc-ref-modal__btn bc-ref-modal__btn--secondary" onClick={handleGenerate}>
                    Regenerate
                  </button>
                </div>
              </>
            )}
          </section>
        </div>

        <div className="bc-ref-modal__footer">
          <button className="bc-ref-modal__btn bc-ref-modal__btn--cancel" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

BCReferenceModal.propTypes = {
  bcRequestNumber: PropTypes.string.isRequired,
  productName: PropTypes.string.isRequired,
  skiRelease: PropTypes.string,
  bcAnswers: PropTypes.object.isRequired,
  onApplyReason: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

BCReferenceModal.defaultProps = {
  skiRelease: null,
};

export default BCReferenceModal;
